import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import * as Yup from "yup";
import {
  getBillingInvoices,
  createManualInvoice,
  downloadBillingInvoicePdf,
  deleteBillingInvoice,
  getCustomerBillingCronRuns,
  updateBillingInvoiceStatus,
  sendBillingInvoiceEmail,
  sendBillingInvoiceReminder,
  refreshCustomerBillingPayments,
  getBillingInvoiceDeliveryStatus,
  syncBillingFromAbra,
} from "app/services/api/billing";

const FIELD_NAME = "BILLING_FIELD";

const VAT_RATES = [23, 19, 5, 0];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Predvolený tvar billing sekcie (dsokSettings.billing). */
export const BILLING_FIELD_SCHEMA = {
  enabled: false,
  billingType: "forward",
  frequency: "monthly",
  annualMonth: 1,
  // Fakturačná doba: neurčitá (default) / určitá. Dátumy platia len pri určitej.
  billingTerm: "indefinite",
  activeFrom: "",
  activeTo: "",
  dueDays: 14,
  billingEmail: "",
  billingPhone: "",
  abraPartnerCode: "",
  // Firemné údaje odberateľa (synchronizovateľné z Abry podľa abraPartnerCode).
  companyName: "",
  street: "",
  city: "",
  psc: "",
  countryCode: "",
  countryName: "",
  ico: "",
  dic: "",
  icDph: "",
  invoiceIntroText: "",
  items: [],
};

/**
 * Validácia — pri ZAPNUTEJ faktúrácii sú kontakt + kľúčové polia povinné.
 * (Táto schéma sa využíva v starom detaile DSOKDetails; nový modulárny detail
 *  má vizuálnu inline validáciu priamo v poli.)
 */
export const getBillingFieldValidationSchema = () => ({
  enabled: Yup.boolean(),
  billingType: Yup.string().when("enabled", {
    is: true,
    then: (s) => s.required("Povinné"),
  }),
  frequency: Yup.string().when("enabled", {
    is: true,
    then: (s) => s.required("Povinné"),
  }),
  dueDays: Yup.number()
    .min(0)
    .when("enabled", { is: true, then: (s) => s.required("Povinné") }),
  billingTerm: Yup.string(),
  // Dátumy sú povinné len pri určitej fakturačnej dobe.
  activeFrom: Yup.string().when(["enabled", "billingTerm"], {
    is: (enabled, term) => enabled === true && term === "fixed",
    then: (s) => s.required("Povinné"),
  }),
  activeTo: Yup.string().when(["enabled", "billingTerm"], {
    is: (enabled, term) => enabled === true && term === "fixed",
    then: (s) => s.required("Povinné"),
  }),
  billingEmail: Yup.string()
    .email("Neplatný email")
    .when("enabled", { is: true, then: (s) => s.required("Povinné") }),
  billingPhone: Yup.string().when("enabled", {
    is: true,
    then: (s) => s.required("Povinné"),
  }),
  annualMonth: Yup.number().min(1).max(12).nullable(),
  items: Yup.array().of(
    Yup.object().shape({
      name: Yup.string(),
      unitPriceMonthly: Yup.number().min(0).nullable(),
      quantity: Yup.number().min(1).nullable(),
    }),
  ),
});

/** Predvolený názov novej položky (premenné {{MM}}/{{RRRR}} doplní Abra/render pri fakturácii). */
const DEFAULT_ITEM_NAME = "Datavision Basic {{MM}}/{{RRRR}}";

const createEmptyItem = () => ({
  id: `item-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
  name: DEFAULT_ITEM_NAME,
  unitPriceMonthly: "",
  quantity: 1,
  vatRate: 23,
  discount: { type: "none", value: 0 },
  period: { type: "always", from: "", to: "" },
  active: true,
});

/** Neaktívna položka zostáva v zozname, ale nefakturuje sa ani sa nezaratáva do súčtov. */
const isItemActive = (it) => !it || it.active !== false;

/**
 * Informatívny súčet položiek (bez DPH / DPH / s DPH). Zrkadlí backend computeItemPreview
 * (ročná = mesačná × 12, zľava percent/fixná). AUTORITATÍVNE počíta Abra — toto je len náhľad.
 */
function computeItemsTotals(items, isAnnual) {
  const months = isAnnual ? 12 : 1;
  const round2 = (n) => Math.round(n * 100) / 100;
  let net = 0;
  let vat = 0;
  (items || []).forEach((it) => {
    if (!isItemActive(it)) return; // neaktívne položky sa nezaratávajú
    const price = parseFloat(String(it.unitPriceMonthly).replace(",", ".")) || 0;
    const qty = Number(it.quantity) || 0;
    const base = price * qty * months;
    const d = it.discount || {};
    let n = base;
    if (d.type === "percent") n = base * (1 - (Number(d.value) || 0) / 100);
    else if (d.type === "fixed")
      n = Math.max(0, base - (Number(d.value) || 0) * months);
    const rate = Number(it.vatRate) || 0;
    net += n;
    vat += n * (rate / 100);
  });
  net = round2(net);
  vat = round2(vat);
  return { net, vat, total: round2(net + vat) };
}

/** ISO → DD.MM.RRRR. */
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getDate()).padStart(2, "0")}.${String(
    d.getMonth() + 1,
  ).padStart(2, "0")}.${d.getFullYear()}`;
};

/** ISO → DD.MM.RRRR HH:MM. */
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${fmtDate(iso)} ${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}`;
};

const STATUS_COLOR = {
  created: "info",
  sent: "info",
  paid: "success",
  overdue: "warning",
  cancelled: "default",
  skipped: "default",
  error: "error",
};

/**
 * Interpretuje reálny stav doručenia jednej delivery (z notif/Brevo) → { label, color, detail, at }.
 * @param {object|null} delivery - jeden NotificationDelivery (channel/status/deliveryStatus/…)
 */
function interpretDelivery(delivery) {
  if (!delivery) {
    // odoslané, ale bez delivery záznamu (staršie odoslanie / notif nedostupný / TTL)
    return { label: "Odoslané", color: "info", detail: "bez potvrdenia doručenia" };
  }
  // Brevo eventy sa líšia (email: delivered/hardBounces/opened/clicks…, sms: delivered/…)
  // → robustne cez substring.
  const ds = String(delivery.deliveryStatus || "").toLowerCase();
  if (ds.includes("deliver") || ds.includes("open") || ds.includes("click")) {
    return { label: "Doručené", color: "success", at: delivery.deliveryStatusAt };
  }
  if (
    ds.includes("bounce") ||
    ds.includes("block") ||
    ds.includes("spam") ||
    ds.includes("invalid") ||
    ds.includes("reject") ||
    ds.includes("defer") ||
    ds.includes("error") ||
    ds.includes("unsub")
  ) {
    return { label: "Nedoručené", color: "error", detail: ds };
  }
  if (delivery.status === "failed") {
    return { label: "Zlyhalo", color: "error", detail: delivery.error };
  }
  if (delivery.status === "sent") {
    return { label: "Odoslané (čaká na doručenie)", color: "warning" };
  }
  return { label: "Čaká", color: "default" };
}

/** Stav doručenia posledného odoslania FAKTÚRY emailom (stĺpec v zozname). */
function interpretEmailDelivery(entry) {
  if (!entry || !entry.data) return null;
  const email = (entry.data.deliveries || []).find((x) => x.channel === "email");
  return interpretDelivery(email);
}

/** Stav doručenia jedného záznamu histórie (podľa jeho kanála). */
function interpretHistoryEntry(e) {
  const list = Array.isArray(e?.deliveries) ? e.deliveries : [];
  const del = list.find((x) => x.channel === e.channel) || list[0] || null;
  return interpretDelivery(del);
}

/** Stavy faktúry, ktoré sa dajú nastaviť manuálne v Datavision Cloud. */
const MANUAL_STATUSES = [
  { value: "created", label: "Vytvorená" },
  { value: "sent", label: "Odoslaná" },
  { value: "paid", label: "Zaplatená" },
  { value: "overdue", label: "Po splatnosti" },
  { value: "cancelled", label: "Stornovaná" },
  { value: "error", label: "Chyba" },
];

const BillingField = ({
  handleChange,
  expanded,
  customerId,
  hideSectionTitle,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const { enqueueSnackbar } = useSnackbar();

  const billing = values.billing || {};
  const items = Array.isArray(billing.items) ? billing.items : [];
  const enabled = billing.enabled === true;
  const isAnnual = billing.frequency === "annual";
  // Určitá fakturačná doba → zobraz dátumy Aktívne od/do.
  const isFixedTerm = billing.billingTerm === "fixed";
  // Informatívny súčet položiek (autoritatívne počíta Abra; do Abry sa neprenáša).
  const previewTotals = React.useMemo(
    () => computeItemsTotals(items, isAnnual),
    [items, isAnnual],
  );

  // Inline validácia povinných polí (platí len keď je faktúrácia zapnutá).
  const reqText = t("pages.dsokBilling.required", { defaultValue: "Povinné" });
  const missingWhenEnabled = (v) =>
    enabled && (v == null || String(v).trim() === "");
  const emailInvalid =
    enabled &&
    (!billing.billingEmail ||
      !/^\S+@\S+\.\S+$/.test(String(billing.billingEmail).trim()));

  const set = (field, val) => setFieldValue(`billing.${field}`, val);
  const setItem = (idx, field, val) =>
    setFieldValue(`billing.items[${idx}].${field}`, val);

  const addItem = () => set("items", [...items, createEmptyItem()]);
  const removeItem = (idx) =>
    set(
      "items",
      items.filter((_, i) => i !== idx),
    );
  // Zneaktívnenie/aktivovanie položky — zostáva v zozname, ale nefakturuje sa ani nezaratáva.
  const toggleItemActive = (idx) => setItem(idx, "active", !isItemActive(items[idx]));

  // --- Faktúry ---
  const [invoices, setInvoices] = useState([]);
  // Reálny stav doručenia emailu per faktúra (z notif/Brevo), lazily na viditeľnej stránke.
  const [deliveryMap, setDeliveryMap] = useState({});
  const deliveryFetchedRef = useRef(new Set());
  // Rozbalený riadok histórie odoslaní (id faktúry alebo null).
  const [historyOpenId, setHistoryOpenId] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [reminderSendingId, setReminderSendingId] = useState(null);
  const [paymentSyncing, setPaymentSyncing] = useState(false);
  const [sendConfirmInv, setSendConfirmInv] = useState(null);
  const [reminderChannelInv, setReminderChannelInv] = useState(null);
  const [reminderConfirm, setReminderConfirm] = useState(null); // { inv, channel }
  const [invPage, setInvPage] = useState(0);
  const [invSearch, setInvSearch] = useState("");
  const INV_PER_PAGE = 10;

  // Filter faktúr podľa čísla faktúry (a fallback podľa obdobia).
  const filteredInvoices = React.useMemo(() => {
    const q = invSearch.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((inv) => {
      const num = String(inv.invoiceNumber || "").toLowerCase();
      const period = String(inv.periodLabel || inv.periodKey || "").toLowerCase();
      return num.includes(q) || period.includes(q);
    });
  }, [invoices, invSearch]);
  const [invForm, setInvForm] = useState({
    frequency: "monthly",
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  });
  const [overrides, setOverrides] = useState({});

  const loadInvoices = useCallback(async () => {
    if (!customerId) return;
    setLoadingInvoices(true);
    try {
      const res = await getBillingInvoices(customerId);
      setInvoices(Array.isArray(res?.data) ? res.data : []);
      // reset delivery cache → po reloade sa reálny stav doručenia načíta nanovo
      deliveryFetchedRef.current = new Set();
      setDeliveryMap({});
    } catch (e) {
      setInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  }, [customerId]);

  // Lazily načítaj reálny stav doručenia pre odoslané faktúry na viditeľnej stránke.
  useEffect(() => {
    if (!active) return;
    const page = filteredInvoices.slice(
      invPage * INV_PER_PAGE,
      invPage * INV_PER_PAGE + INV_PER_PAGE,
    );
    page.forEach((inv) => {
      if (!inv.emailSentAt) return;
      if (deliveryFetchedRef.current.has(inv._id)) return;
      deliveryFetchedRef.current.add(inv._id);
      setDeliveryMap((m) => ({ ...m, [inv._id]: { loading: true } }));
      getBillingInvoiceDeliveryStatus(inv._id)
        .then((res) =>
          setDeliveryMap((m) => ({
            ...m,
            [inv._id]: { loading: false, data: res?.data },
          })),
        )
        .catch(() =>
          setDeliveryMap((m) => ({
            ...m,
            [inv._id]: { loading: false, error: true },
          })),
        );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredInvoices, invPage]);

  // --- Behy faktúrácie (cron/manuál) dotýkajúce sa tohto zákazníka ---
  const [cronRuns, setCronRuns] = useState([]);
  const loadCronRuns = useCallback(async () => {
    if (!customerId) return;
    try {
      const res = await getCustomerBillingCronRuns(customerId, 10);
      setCronRuns(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setCronRuns([]);
    }
  }, [customerId]);

  const active = hideSectionTitle || expanded === FIELD_NAME;
  useEffect(() => {
    if (active && customerId) {
      loadInvoices();
      loadCronRuns();
    }
  }, [active, customerId, loadInvoices, loadCronRuns]);

  /** Časová os dokumentu: vytvorená → odoslania (s reálnym doručením) → zaplatená. */
  const renderInvoiceHistory = (inv) => {
    const entry = deliveryMap[inv._id];
    const sends = Array.isArray(entry?.data?.entries) ? entry.data.entries : [];
    const events = [];
    if (inv.createdAt) events.push({ at: inv.createdAt, kind: "created" });

    if (sends.length) {
      // Máme detailné záznamy z notif servera (kanál, príjemca, reálne doručenie).
      sends.forEach((s) => events.push({ at: s.at, kind: "send", send: s }));
    } else {
      // Fallback z údajov faktúry, keď notif záznamy nie sú (TTL / notif nedostupný).
      if (inv.emailSentAt) {
        events.push({
          at: inv.emailSentAt,
          kind: "send",
          send: { at: inv.emailSentAt, type: "invoice", channel: "email" },
        });
      }
      const rdates = Array.isArray(inv.reminderDates) ? inv.reminderDates : [];
      rdates.forEach((d) =>
        events.push({
          at: d,
          kind: "send",
          send: { at: d, type: "reminder", channel: "email" },
        }),
      );
    }

    if (inv.paidAt) events.push({ at: inv.paidAt, kind: "paid" });
    events.sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));

    const loading = entry?.loading;

    return (
      <Box sx={{ py: 1.5, pl: 2, pr: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("pages.dsokBilling.docHistory", {
            defaultValue: "História dokumentu",
          })}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {t("pages.dsokBilling.histWhen", { defaultValue: "Dátum a čas" })}
              </TableCell>
              <TableCell>
                {t("pages.dsokBilling.histEvent", { defaultValue: "Udalosť" })}
              </TableCell>
              <TableCell>
                {t("pages.dsokBilling.histChannel", { defaultValue: "Kanál" })}
              </TableCell>
              <TableCell>
                {t("pages.dsokBilling.histTo", { defaultValue: "Príjemca" })}
              </TableCell>
              <TableCell>
                {t("pages.dsokBilling.histDelivery", {
                  defaultValue: "Doručenie",
                })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((ev, i) => {
              if (ev.kind === "created") {
                return (
                  <TableRow key={i}>
                    <TableCell>{fmtDateTime(ev.at)}</TableCell>
                    <TableCell>
                      {t("pages.dsokBilling.histCreated", {
                        defaultValue: "Faktúra vytvorená",
                      })}
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                  </TableRow>
                );
              }
              if (ev.kind === "paid") {
                return (
                  <TableRow key={i}>
                    <TableCell>{fmtDateTime(ev.at)}</TableCell>
                    <TableCell>
                      {t("pages.dsokBilling.histPaid", {
                        defaultValue: "Faktúra zaplatená",
                      })}
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={t("pages.dsokBilling.histPaidChip", {
                          defaultValue: "Úhrada",
                        })}
                      />
                    </TableCell>
                  </TableRow>
                );
              }
              const s = ev.send;
              const di = interpretHistoryEntry(s);
              const isReminder = s.type === "reminder";
              return (
                <TableRow key={i}>
                  <TableCell>{fmtDateTime(ev.at)}</TableCell>
                  <TableCell>
                    {isReminder
                      ? t("pages.dsokBilling.histReminderSent", {
                          defaultValue: "Upomienka odoslaná",
                        })
                      : t("pages.dsokBilling.histInvoiceSent", {
                          defaultValue: "Faktúra odoslaná",
                        })}
                    {s.attached ? " (PDF)" : ""}
                  </TableCell>
                  <TableCell>{s.channel === "sms" ? "SMS" : "Email"}</TableCell>
                  <TableCell>{s.to || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={di.color}
                      variant="outlined"
                      label={di.label}
                      title={di.detail || (di.at ? fmtDateTime(di.at) : "")}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {loading && (
          <Typography variant="caption" color="text.secondary">
            {t("pages.dsokBilling.histLoading", {
              defaultValue: "Načítavam stav doručenia…",
            })}
          </Typography>
        )}
        {entry?.error && (
          <Typography variant="caption" color="error">
            {t("pages.dsokBilling.histLoadError", {
              defaultValue: "Stav doručenia sa nepodarilo načítať.",
            })}
          </Typography>
        )}
      </Box>
    );
  };

  const reasonToMessage = (reason) => {
    const map = {
      already_exists: t("pages.dsokBilling.errAlreadyExists", {
        defaultValue: "Faktúra za toto obdobie už existuje.",
      }),
      no_items: t("pages.dsokBilling.errNoItems", {
        defaultValue: "Žiadne položky na fakturáciu v tomto období.",
      }),
      billing_not_configured: t("pages.dsokBilling.errNotConfigured", {
        defaultValue: "Faktúrácia nie je nastavená. Najprv ulož zákazníka.",
      }),
      customer_not_found: t("pages.dsokBilling.errCustomer", {
        defaultValue: "Zákazník sa nenašiel.",
      }),
      invalid_month: t("pages.dsokBilling.errMonth", {
        defaultValue: "Neplatný mesiac.",
      }),
    };
    return (
      map[reason] ||
      t("pages.dsokBilling.errGeneric", {
        defaultValue: "Faktúru sa nepodarilo vytvoriť.",
      })
    );
  };

  const openInvoiceDialog = () => {
    const n = new Date();
    setInvForm({
      frequency: billing.frequency || "monthly",
      year: n.getFullYear(),
      month: n.getMonth() + 1,
    });
    setOverrides({});
    setDialogOpen(true);
  };

  const handleCreateInvoice = async () => {
    setCreating(true);
    try {
      const cleanOverrides = {};
      Object.entries(overrides).forEach(([id, val]) => {
        if (val !== "" && val != null) {
          cleanOverrides[id] = { unitPriceMonthly: val };
        }
      });
      const payload = {
        frequency: invForm.frequency,
        year: Number(invForm.year),
        overrides: cleanOverrides,
      };
      if (invForm.frequency === "monthly") payload.month = Number(invForm.month);

      const res = await createManualInvoice(customerId, payload);
      if (res?.success) {
        enqueueSnackbar(
          t("pages.dsokBilling.invoiceCreated", {
            defaultValue: "Faktúra vytvorená.",
          }),
          { variant: "success" },
        );
        setDialogOpen(false);
        setInvPage(0);
        loadInvoices();
      } else {
        enqueueSnackbar(reasonToMessage(res?.reason), { variant: "error" });
      }
    } catch (e) {
      const data = e?.response?.data;
      const msg =
        data?.reason === "abra_failed" && data?.error
          ? `Abra: ${data.error}`
          : reasonToMessage(data?.reason);
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadPdf = async (inv) => {
    setPdfLoadingId(inv._id);
    try {
      const blob = await downloadBillingInvoicePdf(inv._id);
      // Otvor PDF v novom okne/karte (nie stiahnutie).
      const pdfBlob =
        blob instanceof Blob ? blob.slice(0, blob.size, "application/pdf") : blob;
      const url = window.URL.createObjectURL(pdfBlob);
      window.open(url, "_blank", "noopener,noreferrer");
      // uvoľni blob URL neskôr (okno si ho stihne načítať)
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      enqueueSnackbar(
        t("pages.dsokBilling.pdfError", {
          defaultValue:
            "PDF sa nepodarilo otvoriť (mock faktúra alebo Abra nedostupná).",
        }),
        { variant: "error" },
      );
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleDeleteInvoice = async (inv) => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        t("pages.dsokBilling.deleteConfirm", {
          defaultValue:
            "Zmazať túto faktúru zo zoznamu? (v Abre ostáva – len lokálny záznam)",
        }),
      )
    ) {
      return;
    }
    setDeletingId(inv._id);
    try {
      await deleteBillingInvoice(inv._id);
      enqueueSnackbar(
        t("pages.dsokBilling.deleted", { defaultValue: "Faktúra zmazaná." }),
        { variant: "success" },
      );
      loadInvoices();
    } catch (e) {
      enqueueSnackbar(
        t("pages.dsokBilling.deleteError", {
          defaultValue: "Faktúru sa nepodarilo zmazať.",
        }),
        { variant: "error" },
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (inv, newStatus) => {
    if (!newStatus || newStatus === inv.status) return;
    setStatusSavingId(inv._id);
    // Optimistická zmena v zozname.
    setInvoices((prev) =>
      prev.map((x) => (x._id === inv._id ? { ...x, status: newStatus } : x)),
    );
    try {
      await updateBillingInvoiceStatus(inv._id, newStatus);
      enqueueSnackbar(
        t("pages.dsokBilling.statusChanged", {
          defaultValue: "Stav faktúry zmenený.",
        }),
        { variant: "success" },
      );
    } catch (e) {
      // revert
      setInvoices((prev) =>
        prev.map((x) =>
          x._id === inv._id ? { ...x, status: inv.status } : x,
        ),
      );
      enqueueSnackbar(
        t("pages.dsokBilling.statusChangeError", {
          defaultValue: "Stav sa nepodarilo zmeniť.",
        }),
        { variant: "error" },
      );
    } finally {
      setStatusSavingId(null);
    }
  };

  const SEND_ERR_MAP = {
    email_disabled: "Odosielanie je zatiaľ vypnuté.",
    invoice_sending_disabled:
      "Odosielanie faktúr je vypnuté v systémových nastaveniach faktúrácie.",
    reminder_sending_disabled:
      "Odosielanie upomienok je vypnuté v systémových nastaveniach faktúrácie.",
    no_billing_email: "Zákazník nemá vyplnený email na faktúry.",
    no_billing_phone: "Zákazník nemá vyplnené telefónne číslo.",
    notification_not_configured: "Notifikačný server nie je nakonfigurovaný.",
    pdf_failed: "Nepodarilo sa získať PDF faktúry z Abry.",
    notif_failed: "Notifikačný server odmietol požiadavku.",
    not_overdue: "Faktúra nie je po splatnosti.",
  };

  const handleRefreshCustomerPayments = async () => {
    if (!customerId) return;
    setPaymentSyncing(true);
    try {
      const res = await refreshCustomerBillingPayments(customerId);
      const s = res?.data || {};
      enqueueSnackbar(
        t("pages.dsokBilling.paymentsDone", {
          defaultValue: `Stav úhrady z Abry aktualizovaný — skontrolované: ${
            s.checked ?? 0
          }, zaplatené: ${s.paid ?? 0}, chyby: ${s.errors ?? 0}`,
        }),
        { variant: s.errors > 0 ? "warning" : "success" },
      );
      loadInvoices();
    } catch (e) {
      enqueueSnackbar(
        t("pages.dsokBilling.paymentsError", {
          defaultValue: "Import stavu úhrady z Abry zlyhal.",
        }),
        { variant: "error" },
      );
    } finally {
      setPaymentSyncing(false);
    }
  };

  // Synchronizácia firemných údajov z Abry podľa abraPartnerCode → naplní billing polia.
  const [abraSyncing, setAbraSyncing] = useState(false);
  const handleSyncFromAbra = async () => {
    const code = String(billing.abraPartnerCode || "").trim();
    if (!customerId || !code) return;
    setAbraSyncing(true);
    try {
      const res = await syncBillingFromAbra(customerId, code);
      const d = res?.data;
      if (!res?.success || !d) {
        enqueueSnackbar(
          t("pages.dsokBilling.abraSyncFail", {
            defaultValue: "Údaje z Abry sa nepodarilo načítať.",
          }),
          { variant: "error" },
        );
        return;
      }
      // Prepíš všetky polia hodnotami z Abry (Abra = zdroj pravdy).
      const fields = [
        "companyName",
        "street",
        "city",
        "psc",
        "countryCode",
        "countryName",
        "ico",
        "dic",
        "icDph",
        "billingEmail",
        "billingPhone",
      ];
      fields.forEach((f) => set(f, d[f] != null ? String(d[f]) : ""));
      enqueueSnackbar(
        t("pages.dsokBilling.abraSyncDone", {
          defaultValue:
            "Údaje načítané z Abry. Skontroluj a ulož zmeny tlačidlom Uložiť.",
        }),
        { variant: "success" },
      );
    } catch (e) {
      const reason = e?.response?.data?.reason;
      const map = {
        abra_partner_not_found: "Odberateľ s týmto kódom sa v Abre nenašiel.",
        no_abra_code: "Nie je vyplnený Abra kód odberateľa.",
        abra_not_configured: "Abra nie je nakonfigurovaná.",
      };
      enqueueSnackbar(
        map[reason] ||
          t("pages.dsokBilling.abraSyncFail", {
            defaultValue: "Údaje z Abry sa nepodarilo načítať.",
          }),
        { variant: "error" },
      );
    } finally {
      setAbraSyncing(false);
    }
  };

  const doSendInvoice = async (inv, force = false) => {
    setSendConfirmInv(null);
    setSendingId(inv._id);
    try {
      const res = await sendBillingInvoiceEmail(inv._id, force);
      if (res?.success) {
        enqueueSnackbar(
          t("pages.dsokBilling.sent", {
            defaultValue: res.attached
              ? "Faktúra odoslaná emailom (s PDF)."
              : "Faktúra odoslaná emailom.",
          }),
          { variant: "success" },
        );
        loadInvoices();
      }
    } catch (e) {
      const reason = e?.response?.data?.reason;
      if (reason === "already_sent") {
        // Už raz odoslaná → potvrdenie, či poslať znova.
        setSendConfirmInv({
          ...inv,
          emailSentAt: e?.response?.data?.emailSentAt || inv.emailSentAt,
        });
        return;
      }
      enqueueSnackbar(
        SEND_ERR_MAP[reason] ||
          t("pages.dsokBilling.sendError", {
            defaultValue: "Faktúru sa nepodarilo odoslať.",
          }),
        { variant: reason === "email_disabled" ? "info" : "error" },
      );
    } finally {
      setSendingId(null);
    }
  };

  // Upomienka cez zvolený kanál (email/sms). force=true po potvrdení (už dnes zaslaná).
  const handleSendReminder = async (inv, channel, force = false) => {
    setReminderChannelInv(null);
    setReminderConfirm(null);
    setReminderSendingId(inv._id);
    try {
      const res = await sendBillingInvoiceReminder(inv._id, channel, force);
      if (res?.success) {
        enqueueSnackbar(
          t("pages.dsokBilling.reminderSent", {
            defaultValue:
              channel === "sms"
                ? "Upomienka odoslaná SMS-kou."
                : "Upomienka odoslaná emailom.",
          }),
          { variant: "success" },
        );
        loadInvoices();
      }
    } catch (e) {
      const reason = e?.response?.data?.reason;
      if (reason === "already_sent_today") {
        // Už bola dnes zaslaná → potvrdenie, či zaslať znova (s tým istým kanálom).
        setReminderConfirm({ inv, channel });
        return;
      }
      enqueueSnackbar(
        SEND_ERR_MAP[reason] ||
          t("pages.dsokBilling.reminderError", {
            defaultValue: "Upomienku sa nepodarilo odoslať.",
          }),
        { variant: reason === "email_disabled" ? "info" : "error" },
      );
    } finally {
      setReminderSendingId(null);
    }
  };

  return (
    <Accordion
      expanded={hideSectionTitle ? true : expanded === FIELD_NAME}
      onChange={hideSectionTitle ? undefined : handleChange(FIELD_NAME)}
      disableGutters={hideSectionTitle || undefined}
      sx={
        hideSectionTitle
          ? {
              boxShadow: "none",
              bgcolor: "transparent",
              "&:before": { display: "none" },
            }
          : undefined
      }
    >
      {!hideSectionTitle && (
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ width: "33%", flexShrink: 0 }}>
            {t("pages.dsokBilling.title", { defaultValue: "Faktúrácia" })}
          </Typography>
        </AccordionSummary>
      )}
      <AccordionDetails sx={hideSectionTitle ? { px: 0 } : undefined}>
        {/* --- Nastavenia faktúrácie --- */}
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => set("enabled", e.target.checked)}
            />
          }
          label={t("pages.dsokBilling.enable", {
            defaultValue: "Povoliť faktúráciu",
          })}
        />

        {/* Kontakt na faktúry a SMS/upomienky — povinné pri zapnutej faktúrácii. */}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              type="email"
              fullWidth
              size="small"
              required={enabled}
              label={t("pages.dsokBilling.email", {
                defaultValue: "Email (faktúry)",
              })}
              value={billing.billingEmail || ""}
              onChange={(e) => set("billingEmail", e.target.value)}
              error={emailInvalid}
              helperText={
                emailInvalid
                  ? billing.billingEmail
                    ? t("pages.dsokBilling.emailInvalid", {
                        defaultValue: "Neplatný email",
                      })
                    : reqText
                  : ""
              }
              placeholder="fakturacia@firma.sk"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              required={enabled}
              label={t("pages.dsokBilling.phone", {
                defaultValue: "Telefón (SMS/upomienky)",
              })}
              value={billing.billingPhone || ""}
              onChange={(e) => set("billingPhone", e.target.value)}
              error={missingWhenEnabled(billing.billingPhone)}
              helperText={missingWhenEnabled(billing.billingPhone) ? reqText : ""}
              placeholder="+421..."
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                fullWidth
                size="small"
                label={t("pages.dsokBilling.abraPartnerCode", {
                  defaultValue: "Abra kód odberateľa",
                })}
                value={billing.abraPartnerCode || ""}
                onChange={(e) => set("abraPartnerCode", e.target.value)}
                placeholder="napr. FIRMA123"
              />
              {String(billing.abraPartnerCode || "").trim() !== "" && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSyncFromAbra}
                  disabled={abraSyncing}
                  startIcon={
                    abraSyncing ? (
                      <CircularProgress size={16} />
                    ) : (
                      <PaidOutlinedIcon />
                    )
                  }
                  sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {t("pages.dsokBilling.syncFromAbra", {
                    defaultValue: "Synchronizovať údaje",
                  })}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Firemné údaje odberateľa (synchronizovateľné z Abry). */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
          {t("pages.dsokBilling.companyDetails", {
            defaultValue: "Firemné údaje odberateľa",
          })}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.companyName", {
                defaultValue: "Názov firmy",
              })}
              value={billing.companyName || ""}
              onChange={(e) => set("companyName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.street", { defaultValue: "Ulica" })}
              value={billing.street || ""}
              onChange={(e) => set("street", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.city", { defaultValue: "Mesto" })}
              value={billing.city || ""}
              onChange={(e) => set("city", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.psc", { defaultValue: "PSČ" })}
              value={billing.psc || ""}
              onChange={(e) => set("psc", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.country", {
                defaultValue: "Krajina",
              })}
              value={billing.countryName || ""}
              onChange={(e) => set("countryName", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.ico", { defaultValue: "IČO" })}
              value={billing.ico || ""}
              onChange={(e) => set("ico", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.dic", { defaultValue: "DIČ" })}
              value={billing.dic || ""}
              onChange={(e) => set("dic", e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth
              size="small"
              label={t("pages.dsokBilling.icDph", { defaultValue: "IČ DPH" })}
              value={billing.icDph || ""}
              onChange={(e) => set("icDph", e.target.value)}
            />
          </Grid>
        </Grid>

        {/* Parametre faktúrácie — užšie polia. */}
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              required={enabled}
              label={t("pages.dsokBilling.type", {
                defaultValue: "Typ faktúrácie",
              })}
              value={billing.billingType || "forward"}
              onChange={(e) => set("billingType", e.target.value)}
            >
              <MenuItem value="forward">
                {t("pages.dsokBilling.forward", { defaultValue: "Dopredu" })}
              </MenuItem>
              <MenuItem value="backward">
                {t("pages.dsokBilling.backward", { defaultValue: "Dozadu" })}
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              required={enabled}
              label={t("pages.dsokBilling.frequency", {
                defaultValue: "Frekvencia",
              })}
              value={billing.frequency || "monthly"}
              onChange={(e) => set("frequency", e.target.value)}
            >
              <MenuItem value="monthly">
                {t("pages.dsokBilling.monthly", { defaultValue: "Mesačná" })}
              </MenuItem>
              <MenuItem value="annual">
                {t("pages.dsokBilling.annual", { defaultValue: "Ročná" })}
              </MenuItem>
            </TextField>
          </Grid>

          {isAnnual && (
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("pages.dsokBilling.annualMonth", {
                  defaultValue: "Mesiac ročnej faktúry",
                })}
                value={billing.annualMonth || 1}
                onChange={(e) => set("annualMonth", Number(e.target.value))}
              >
                {MONTHS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label={t("pages.dsokBilling.billingTerm", {
                defaultValue: "Fakturačná doba",
              })}
              value={billing.billingTerm || "indefinite"}
              onChange={(e) => set("billingTerm", e.target.value)}
            >
              <MenuItem value="indefinite">
                {t("pages.dsokBilling.termIndefinite", {
                  defaultValue: "Neurčitá",
                })}
              </MenuItem>
              <MenuItem value="fixed">
                {t("pages.dsokBilling.termFixed", { defaultValue: "Určitá" })}
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              type="number"
              fullWidth
              size="small"
              required={enabled}
              label={t("pages.dsokBilling.dueDays", {
                defaultValue: "Splatnosť (dni)",
              })}
              value={billing.dueDays ?? 14}
              onChange={(e) => set("dueDays", e.target.value)}
              error={missingWhenEnabled(billing.dueDays)}
              helperText={missingWhenEnabled(billing.dueDays) ? reqText : ""}
            />
          </Grid>

          {/* Dátumy len pri určitej fakturačnej dobe (Aktívne od sa zobrazí len keď je dátum ukončenia). */}
          {isFixedTerm && (
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                type="date"
                fullWidth
                size="small"
                required={enabled}
                InputLabelProps={{ shrink: true }}
                label={t("pages.dsokBilling.activeFrom", {
                  defaultValue: "Aktívne od",
                })}
                value={billing.activeFrom || ""}
                onChange={(e) => set("activeFrom", e.target.value)}
                error={enabled && !String(billing.activeFrom || "").trim()}
                helperText={
                  enabled && !String(billing.activeFrom || "").trim()
                    ? reqText
                    : ""
                }
              />
            </Grid>
          )}

          {isFixedTerm && (
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                type="date"
                fullWidth
                size="small"
                required={enabled}
                InputLabelProps={{ shrink: true }}
                label={t("pages.dsokBilling.activeTo", {
                  defaultValue: "Aktívne do",
                })}
                value={billing.activeTo || ""}
                onChange={(e) => set("activeTo", e.target.value)}
                error={enabled && !String(billing.activeTo || "").trim()}
                helperText={
                  enabled && !String(billing.activeTo || "").trim()
                    ? reqText
                    : ""
                }
              />
            </Grid>
          )}
        </Grid>

        {/* --- Úvodný text faktúry --- */}
        <Box sx={{ mt: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label={t("pages.dsokBilling.introText", {
              defaultValue: "Úvodný text faktúry",
            })}
            value={billing.invoiceIntroText || ""}
            onChange={(e) => set("invoiceIntroText", e.target.value)}
            placeholder="napr. Fakturujeme Vám za mesiac {{MM}}/{{RRRR}}"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.75 }}
          >
            Premenné (dátum vystavenia): {"{{DD}}"} = deň, {"{{MM}}"} = mesiac,{" "}
            {"{{RRRR}}"} = rok (4-cif.), {"{{RR}}"} = rok (2-cif.). Použiteľné v
            úvodnom texte aj v názvoch položiek. Záverečný text je pevný: „V prípade
            nedodržania splatnosti si účtujeme 0,5% za každý deň omeškania.“
          </Typography>
        </Box>

        {/* --- Fakturované položky --- */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 3, mb: 1 }}
        >
          <Typography variant="h6">
            {t("pages.dsokBilling.items", {
              defaultValue: "Fakturované položky",
            })}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addItem}
          >
            {t("pages.dsokBilling.addItem", { defaultValue: "Pridať položku" })}
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t("pages.dsokBilling.itemsHint", {
            defaultValue:
              "Jednotková cena je vždy MESAČNE (ročná faktúra = cena × 12). Sumy a DPH počíta Abra.",
          })}
        </Typography>

        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t("pages.dsokBilling.noItems", {
              defaultValue: "Zatiaľ žiadne položky.",
            })}
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 200 }}>
                    {t("pages.dsokBilling.colName", { defaultValue: "Názov" })}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    {t("pages.dsokBilling.colPrice", {
                      defaultValue: "Cena/mes (€)",
                    })}
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    {t("pages.dsokBilling.colQty", { defaultValue: "Množstvo" })}
                  </TableCell>
                  <TableCell sx={{ minWidth: 90 }}>
                    {t("pages.dsokBilling.colVat", { defaultValue: "DPH" })}
                  </TableCell>
                  <TableCell sx={{ minWidth: 200 }}>
                    {t("pages.dsokBilling.colDiscount", {
                      defaultValue: "Zľava",
                    })}
                  </TableCell>
                  <TableCell sx={{ minWidth: 220 }}>
                    {t("pages.dsokBilling.colPeriod", {
                      defaultValue: "Obdobie",
                    })}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => {
                  const discount = item.discount || { type: "none", value: 0 };
                  const period = item.period || { type: "always" };
                  const active = isItemActive(item);
                  return (
                    <TableRow
                      key={item.id || idx}
                      sx={active ? undefined : { opacity: 0.5 }}
                    >
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={item.name || ""}
                          onChange={(e) => setItem(idx, "name", e.target.value)}
                          helperText={
                            active
                              ? undefined
                              : t("pages.dsokBilling.itemInactiveHint", {
                                  defaultValue: "Neaktívna — nefakturuje sa",
                                })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.unitPriceMonthly ?? ""}
                          onChange={(e) =>
                            setItem(idx, "unitPriceMonthly", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={item.quantity ?? 1}
                          onChange={(e) =>
                            setItem(idx, "quantity", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          value={item.vatRate ?? 23}
                          onChange={(e) =>
                            setItem(idx, "vatRate", Number(e.target.value))
                          }
                        >
                          {VAT_RATES.map((v) => (
                            <MenuItem key={v} value={v}>
                              {v} %
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <TextField
                            select
                            size="small"
                            sx={{ minWidth: 100 }}
                            value={discount.type || "none"}
                            onChange={(e) =>
                              setItem(idx, "discount", {
                                ...discount,
                                type: e.target.value,
                                value:
                                  e.target.value === "none"
                                    ? 0
                                    : discount.value || 0,
                              })
                            }
                          >
                            <MenuItem value="none">
                              {t("pages.dsokBilling.discountNone", {
                                defaultValue: "Žiadna",
                              })}
                            </MenuItem>
                            <MenuItem value="percent">%</MenuItem>
                            <MenuItem value="fixed">€</MenuItem>
                          </TextField>
                          {discount.type && discount.type !== "none" && (
                            <TextField
                              type="number"
                              size="small"
                              sx={{ width: 90 }}
                              value={discount.value ?? 0}
                              onChange={(e) =>
                                setItem(idx, "discount", {
                                  ...discount,
                                  value: e.target.value,
                                })
                              }
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          <TextField
                            select
                            size="small"
                            value={period.type || "always"}
                            onChange={(e) =>
                              setItem(idx, "period", {
                                ...period,
                                type: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="always">
                              {t("pages.dsokBilling.periodAlways", {
                                defaultValue: "Celoročne",
                              })}
                            </MenuItem>
                            <MenuItem value="range">
                              {t("pages.dsokBilling.periodRange", {
                                defaultValue: "Určité obdobie",
                              })}
                            </MenuItem>
                          </TextField>
                          {period.type === "range" && (
                            <Stack direction="row" spacing={1}>
                              <TextField
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={period.from || ""}
                                onChange={(e) =>
                                  setItem(idx, "period", {
                                    ...period,
                                    from: e.target.value,
                                  })
                                }
                              />
                              <TextField
                                type="date"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={period.to || ""}
                                onChange={(e) =>
                                  setItem(idx, "period", {
                                    ...period,
                                    to: e.target.value,
                                  })
                                }
                              />
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip
                            arrow
                            title={
                              active
                                ? t("pages.dsokBilling.deactivateItem", {
                                    defaultValue:
                                      "Zneaktívniť (zostane v zozname, nefakturuje sa)",
                                  })
                                : t("pages.dsokBilling.activateItem", {
                                    defaultValue: "Aktivovať položku",
                                  })
                            }
                          >
                            <IconButton
                              color={active ? "success" : "default"}
                              size="small"
                              onClick={() => toggleItemActive(idx)}
                            >
                              {active ? (
                                <ToggleOnIcon fontSize="small" />
                              ) : (
                                <ToggleOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            arrow
                            title={t("pages.dsokBilling.deleteItem", {
                              defaultValue: "Vymazať položku",
                            })}
                          >
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => removeItem(idx)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Informatívny súčet za faktúru (neprenáša sa do Abry). */}
        {items.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 1,
            }}
          >
            <Box sx={{ minWidth: 260 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ py: 0.25 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("pages.dsokBilling.totalNet", {
                    defaultValue: "Spolu bez DPH",
                  })}
                </Typography>
                <Typography variant="body2">
                  {previewTotals.net.toFixed(2)} €
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ py: 0.25 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("pages.dsokBilling.totalVat", { defaultValue: "DPH" })}
                </Typography>
                <Typography variant="body2">
                  {previewTotals.vat.toFixed(2)} €
                </Typography>
              </Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ py: 0.25, borderTop: "1px solid", borderColor: "divider" }}
              >
                <Typography variant="subtitle2">
                  {t("pages.dsokBilling.totalGross", {
                    defaultValue: "Spolu s DPH",
                  })}
                </Typography>
                <Typography variant="subtitle2">
                  {previewTotals.total.toFixed(2)} €
                </Typography>
              </Stack>
            </Box>
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {t("pages.dsokBilling.abraNote", {
            defaultValue:
              "Faktúru vytvára Abra Flexi z týchto položiek — Datavision Cloud pripravuje len podklad.",
          })}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {t("pages.dsokBilling.totalsInfoNote", {
            defaultValue:
              "Súčet je informatívny (mesačná × 12 pri ročnej) — do Abry sa neprenáša, sumy počíta Abra.",
          })}
        </Typography>

        {/* --- Faktúry --- */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 4, mb: 1 }}
        >
          <Typography variant="h6">
            {t("pages.dsokBilling.invoices", { defaultValue: "Faktúry" })}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={
                paymentSyncing ? <CircularProgress size={16} /> : <PaidOutlinedIcon />
              }
              onClick={handleRefreshCustomerPayments}
              disabled={!customerId || paymentSyncing}
            >
              {t("pages.dsokBilling.refreshPayments", {
                defaultValue: "Aktualizovať stav z Abry",
              })}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<ReceiptLongIcon />}
              onClick={openInvoiceDialog}
              disabled={!customerId}
            >
              {t("pages.dsokBilling.createManual", {
                defaultValue: "Vytvoriť faktúru ručne",
              })}
            </Button>
          </Stack>
        </Stack>

        {invoices.length > 0 && (
          <TextField
            size="small"
            fullWidth
            value={invSearch}
            onChange={(e) => {
              setInvSearch(e.target.value);
              setInvPage(0);
            }}
            placeholder={t("pages.dsokBilling.searchInvoice", {
              defaultValue: "Hľadať podľa čísla faktúry alebo obdobia…",
            })}
            sx={{ mb: 1 }}
          />
        )}

        {loadingInvoices ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : invoices.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {t("pages.dsokBilling.noInvoices", {
              defaultValue: "Zatiaľ žiadne faktúry.",
            })}
          </Typography>
        ) : filteredInvoices.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {t("pages.dsokBilling.noInvoicesMatch", {
              defaultValue: "Žiadna faktúra nezodpovedá hľadaniu.",
            })}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {t("pages.dsokBilling.invNumber", {
                      defaultValue: "Číslo",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.invPeriod", {
                      defaultValue: "Obdobie",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    {t("pages.dsokBilling.invTotal", {
                      defaultValue: "Suma (€)",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.invStatus", {
                      defaultValue: "Status",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.invCreated", {
                      defaultValue: "Vytvorené",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.invPaidAt", {
                      defaultValue: "Dátum úhrady",
                    })}
                  </TableCell>
                  <TableCell>
                    {t("pages.dsokBilling.invDelivery", {
                      defaultValue: "Doručenie zákazníkovi",
                    })}
                  </TableCell>
                  <TableCell align="center">PDF</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredInvoices
                  .slice(
                    invPage * INV_PER_PAGE,
                    invPage * INV_PER_PAGE + INV_PER_PAGE,
                  )
                  .map((inv) => (
                  <React.Fragment key={inv._id}>
                  <TableRow>
                    <TableCell>{inv.invoiceNumber || "—"}</TableCell>
                    <TableCell>{inv.periodLabel || inv.periodKey}</TableCell>
                    <TableCell align="right">
                      {inv.totalsPreview?.total != null
                        ? inv.totalsPreview.total
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={
                          MANUAL_STATUSES.some((s) => s.value === inv.status)
                            ? inv.status
                            : "created"
                        }
                        onChange={(e) => handleStatusChange(inv, e.target.value)}
                        disabled={statusSavingId === inv._id}
                        variant="standard"
                        sx={{ minWidth: 130 }}
                        SelectProps={{
                          renderValue: (val) => {
                            const opt = MANUAL_STATUSES.find(
                              (s) => s.value === val,
                            );
                            return (
                              <Chip
                                size="small"
                                label={opt ? opt.label : val}
                                color={STATUS_COLOR[val] || "default"}
                                variant="outlined"
                              />
                            );
                          },
                        }}
                      >
                        {MANUAL_STATUSES.map((s) => (
                          <MenuItem key={s.value} value={s.value}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>{fmtDate(inv.createdAt)}</TableCell>
                    <TableCell>
                      {inv.paidAt ? (
                        fmtDate(inv.paidAt)
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.emailSentAt ? (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {fmtDateTime(inv.emailSentAt)}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            {(() => {
                              const entry = deliveryMap[inv._id];
                              if (!entry || entry.loading) {
                                return <CircularProgress size={12} />;
                              }
                              const di = interpretEmailDelivery(entry);
                              const label = di ? di.label : "Odoslané";
                              const color = di ? di.color : "info";
                              const tip =
                                di && (di.detail || di.at)
                                  ? di.detail || fmtDateTime(di.at)
                                  : "";
                              return (
                                <Chip
                                  size="small"
                                  label={label}
                                  color={color}
                                  variant="outlined"
                                  title={tip}
                                />
                              );
                            })()}
                            {(() => {
                              const open = historyOpenId === inv._id;
                              return (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setHistoryOpenId(open ? null : inv._id)
                                  }
                                  title={t("pages.dsokBilling.deliveryHistory", {
                                    defaultValue: "História dokumentu",
                                  })}
                                >
                                  <ExpandMoreIcon
                                    fontSize="small"
                                    sx={{
                                      transform: open
                                        ? "rotate(180deg)"
                                        : "none",
                                      transition: "transform .2s",
                                    }}
                                  />
                                </IconButton>
                              );
                            })()}
                          </Stack>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {inv.abra?.id && inv.abra?.mock !== true ? (
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={pdfLoadingId === inv._id}
                          onClick={() => handleDownloadPdf(inv)}
                          title={t("pages.dsokBilling.openPdf", {
                            defaultValue: "Otvoriť PDF",
                          })}
                        >
                          {pdfLoadingId === inv._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <PictureAsPdfOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={sendingId === inv._id}
                        onClick={() => setSendConfirmInv(inv)}
                        title={t("pages.dsokBilling.sendInvoice", {
                          defaultValue: "Odoslať faktúru emailom",
                        })}
                      >
                        {sendingId === inv._id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <EmailOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                      {inv.status === "overdue" && (
                        <IconButton
                          size="small"
                          color="warning"
                          disabled={reminderSendingId === inv._id}
                          onClick={() => setReminderChannelInv(inv)}
                          title={t("pages.dsokBilling.sendReminder", {
                            defaultValue: "Odoslať upomienku (email/SMS)",
                          })}
                        >
                          {reminderSendingId === inv._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <NotificationsActiveOutlinedIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deletingId === inv._id}
                        onClick={() => handleDeleteInvoice(inv)}
                        title={t("pages.dsokBilling.deleteInvoice", {
                          defaultValue: "Zmazať faktúru",
                        })}
                      >
                        {deletingId === inv._id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      sx={{ py: 0, borderBottom: "none" }}
                    >
                      <Collapse
                        in={historyOpenId === inv._id}
                        timeout="auto"
                        unmountOnExit
                      >
                        {renderInvoiceHistory(inv)}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredInvoices.length}
              page={invPage}
              onPageChange={(_e, p) => setInvPage(p)}
              rowsPerPage={INV_PER_PAGE}
              rowsPerPageOptions={[INV_PER_PAGE]}
              labelRowsPerPage=""
            />
          </TableContainer>
        )}

        {/* --- Behy faktúrácie (cron/manuál) --- */}
        {cronRuns.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t("pages.dsokBilling.cronRuns", {
                defaultValue: "Posledné behy faktúrácie",
              })}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("pages.dsokBilling.runDate", { defaultValue: "Dátum" })}
                    </TableCell>
                    <TableCell>
                      {t("pages.dsokBilling.runTrigger", {
                        defaultValue: "Spustenie",
                      })}
                    </TableCell>
                    <TableCell>
                      {t("pages.dsokBilling.runStatus", { defaultValue: "Stav" })}
                    </TableCell>
                    <TableCell>
                      {t("pages.dsokBilling.runDetail", {
                        defaultValue: "Detail",
                      })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cronRuns.map((run) => {
                    const r = run.result || {};
                    const status = r.status || "—";
                    return (
                      <TableRow key={run._id}>
                        <TableCell>{fmtDateTime(run.startedAt)}</TableCell>
                        <TableCell>
                          {String(run.trigger || "").startsWith("manual")
                            ? t("pages.dsokBilling.triggerManual", {
                                defaultValue: "Ručne",
                              })
                            : t("pages.dsokBilling.triggerCron", {
                                defaultValue: "Automaticky",
                              })}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={status}
                            color={STATUS_COLOR[status] || "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {r.invoiceNumber ||
                            r.reason ||
                            r.error ||
                            r.periodKey ||
                            ""}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </AccordionDetails>

      {/* --- Dialóg: potvrdenie odoslania faktúry --- */}
      <Dialog open={!!sendConfirmInv} onClose={() => setSendConfirmInv(null)}>
        <DialogTitle>
          {sendConfirmInv?.emailSentAt
            ? t("pages.dsokBilling.sendAgainTitle", {
                defaultValue: "Faktúra už bola odoslaná",
              })
            : t("pages.dsokBilling.sendConfirmTitle", {
                defaultValue: "Odoslať faktúru?",
              })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {sendConfirmInv?.emailSentAt
              ? t("pages.dsokBilling.sendAgainText", {
                  defaultValue: `Faktúra ${
                    sendConfirmInv?.invoiceNumber || ""
                  } už bola odoslaná (${fmtDate(
                    sendConfirmInv?.emailSentAt,
                  )}). Naozaj poslať znova?`,
                })
              : t("pages.dsokBilling.sendConfirmText", {
                  defaultValue: `Naozaj poslať faktúru ${
                    sendConfirmInv?.invoiceNumber || ""
                  } emailom zákazníkovi?`,
                })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendConfirmInv(null)}>
            {t("pages.dsokBilling.cancel", { defaultValue: "Zrušiť" })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              doSendInvoice(sendConfirmInv, !!sendConfirmInv?.emailSentAt)
            }
          >
            {sendConfirmInv?.emailSentAt
              ? t("pages.dsokBilling.sendAgain", { defaultValue: "Poslať znova" })
              : t("pages.dsokBilling.send", { defaultValue: "Odoslať" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Dialóg: výber kanála upomienky (email / SMS) --- */}
      <Dialog
        open={!!reminderChannelInv}
        onClose={() => setReminderChannelInv(null)}
      >
        <DialogTitle>
          {t("pages.dsokBilling.reminderChannelTitle", {
            defaultValue: "Odoslať upomienku",
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("pages.dsokBilling.reminderChannelText", {
              defaultValue: `Ako odoslať upomienku pre faktúru ${
                reminderChannelInv?.invoiceNumber || ""
              }?`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderChannelInv(null)}>
            {t("pages.dsokBilling.cancel", { defaultValue: "Zrušiť" })}
          </Button>
          <Button
            variant="outlined"
            startIcon={<EmailOutlinedIcon />}
            onClick={() => handleSendReminder(reminderChannelInv, "email", false)}
          >
            {t("pages.dsokBilling.email", { defaultValue: "Emailom" })}
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<NotificationsActiveOutlinedIcon />}
            onClick={() => handleSendReminder(reminderChannelInv, "sms", false)}
          >
            {t("pages.dsokBilling.sms", { defaultValue: "SMS-kou" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Dialóg: upomienka už bola dnes zaslaná --- */}
      <Dialog
        open={!!reminderConfirm}
        onClose={() => setReminderConfirm(null)}
      >
        <DialogTitle>
          {t("pages.dsokBilling.reminderConfirmTitle", {
            defaultValue: "Upomienka už bola dnes zaslaná",
          })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("pages.dsokBilling.reminderConfirmText", {
              defaultValue: `Pre faktúru ${
                reminderConfirm?.inv?.invoiceNumber || ""
              } už dnes bola upomienka odoslaná. Naozaj zaslať znova (${
                reminderConfirm?.channel === "sms" ? "SMS" : "email"
              })?`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderConfirm(null)}>
            {t("pages.dsokBilling.cancel", { defaultValue: "Zrušiť" })}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() =>
              handleSendReminder(
                reminderConfirm?.inv,
                reminderConfirm?.channel,
                true,
              )
            }
          >
            {t("pages.dsokBilling.reminderSendAgain", {
              defaultValue: "Zaslať znova",
            })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Dialóg: manuálna faktúra --- */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t("pages.dsokBilling.createManualTitle", {
            defaultValue: "Vytvoriť faktúru ručne",
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("pages.dsokBilling.manualHint", {
              defaultValue:
                "Faktúra sa vytvorí zo strany servera z ULOŽENÝCH nastavení. Ak si menil položky, najprv ulož zákazníka.",
            })}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                size="small"
                label={t("pages.dsokBilling.frequency", {
                  defaultValue: "Frekvencia",
                })}
                value={invForm.frequency}
                onChange={(e) =>
                  setInvForm((p) => ({ ...p, frequency: e.target.value }))
                }
              >
                <MenuItem value="monthly">
                  {t("pages.dsokBilling.monthly", { defaultValue: "Mesačná" })}
                </MenuItem>
                <MenuItem value="annual">
                  {t("pages.dsokBilling.annual", { defaultValue: "Ročná" })}
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField
                type="number"
                fullWidth
                size="small"
                label={t("pages.dsokBilling.year", { defaultValue: "Rok" })}
                value={invForm.year}
                onChange={(e) =>
                  setInvForm((p) => ({ ...p, year: e.target.value }))
                }
              />
            </Grid>
            {invForm.frequency === "monthly" && (
              <Grid item xs={6} sm={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={t("pages.dsokBilling.month", {
                    defaultValue: "Mesiac",
                  })}
                  value={invForm.month}
                  onChange={(e) =>
                    setInvForm((p) => ({ ...p, month: Number(e.target.value) }))
                  }
                >
                  {MONTHS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>

          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            {t("pages.dsokBilling.overrideTitle", {
              defaultValue: "Ceny položiek (prázdne = pôvodná cena)",
            })}
          </Typography>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("pages.dsokBilling.noItems", {
                defaultValue: "Zatiaľ žiadne položky.",
              })}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("pages.dsokBilling.colName", {
                        defaultValue: "Názov",
                      })}
                    </TableCell>
                    <TableCell align="right">
                      {t("pages.dsokBilling.origPrice", {
                        defaultValue: "Pôvodná (€/mes)",
                      })}
                    </TableCell>
                    <TableCell align="right">
                      {t("pages.dsokBilling.overridePrice", {
                        defaultValue: "Override (€/mes)",
                      })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.filter(isItemActive).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name || "—"}</TableCell>
                      <TableCell align="right">
                        {item.unitPriceMonthly}
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          placeholder={String(item.unitPriceMonthly ?? "")}
                          value={overrides[item.id] ?? ""}
                          onChange={(e) =>
                            setOverrides((p) => ({
                              ...p,
                              [item.id]: e.target.value,
                            }))
                          }
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={creating}>
            {t("buttons.cancel", { defaultValue: "Zrušiť" })}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateInvoice}
            disabled={creating}
          >
            {creating ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t("pages.dsokBilling.confirm", { defaultValue: "Potvrdiť" })
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};

export default BillingField;
